import React from 'react';
import { Target, BookOpen, Shield, Zap, HeartHandshake, Users, CheckCircle, TrendingUp, Globe } from 'lucide-react';

const values = [
  {
    icon: <Shield size={20} />,
    title: 'Transparency',
    description: 'Clear pricing with no hidden fees. Every transaction is fully auditable.',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-100',
  },
  {
    icon: <Zap size={20} />,
    title: 'Efficiency',
    description: 'Lightning-fast payments across borders with minimal friction.',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-100',
  },
  {
    icon: <Shield size={20} />,
    title: 'Security',
    description: 'Industry-leading security protocols for every payment and data point.',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-100',
  },
  {
    icon: <HeartHandshake size={20} />,
    title: 'Support',
    description: '24/7 dedicated customer support whenever you need assistance.',
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-100',
  },
];

const milestones = [
  { year: '2021', event: 'Founded with a mission to simplify global procurement' },
  { year: '2022', event: 'Launched beta with 50 enterprise clients across West Africa' },
  { year: '2023', event: 'Expanded to 30+ countries, processed ₦500M+ in payments' },
  { year: '2024', event: 'Reached 500+ active businesses, $60M+ processed all time' },
];

const AboutPage: React.FC = () => {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <div className="bg-gradient-to-br from-fintech-primary/85 via-blue-800/78 to-slate-900/72 py-20 border-y border-white/20 backdrop-blur-[2px]">
        <div className="section-container text-center">
          <div className="public-readable-panel public-readable-panel--dark">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 border border-white/20 text-blue-100 text-xs font-semibold uppercase tracking-wider mb-5">
            About Us
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold text-white mb-5 tracking-tight">
            Redefining Global Procurement
          </h1>
          <p className="text-blue-200 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed px-4">
            We're building the infrastructure that powers modern procurement for businesses operating across borders.
          </p>

          {/* Quick stats */}
          <div className="flex flex-wrap items-center justify-center gap-8 mt-10">
            {[
              { icon: <TrendingUp size={16} />, value: '$60M+', label: 'All time processed' },
              { icon: <Users size={16} />, value: '500+', label: 'Active businesses' },
              { icon: <Globe size={16} />, value: '50+', label: 'Countries' },
            ].map((stat, i) => (
              <div key={i} className="flex items-center gap-2 text-white">
                <span className="text-fintech-accent">{stat.icon}</span>
                <span className="font-bold">{stat.value}</span>
                <span className="text-blue-300 text-sm">{stat.label}</span>
              </div>
            ))}
          </div>
          </div>
        </div>
      </div>

      <div className="section-container py-16">
        {/* Mission & Story */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-16">
          <div className="card p-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 text-fintech-primary flex items-center justify-center">
                <Target size={20} />
              </div>
              <h2 className="text-xl font-bold text-fintech-text">Our Mission</h2>
            </div>
            <p className="text-fintech-textMuted leading-relaxed mb-4">
              At Kenluk Procurement, we're revolutionizing global procurement for business professionals.
              Our platform enables seamless vendor management with confidence and transparency, removing
              the friction that slows down international trade.
            </p>
            <ul className="flex flex-col gap-2">
              {['Simplify cross-border payments', 'Reduce procurement cycle time', 'Ensure full compliance & auditability'].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm text-fintech-textMuted">
                  <CheckCircle size={14} className="text-fintech-accent flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="card p-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-600 flex items-center justify-center">
                <BookOpen size={20} />
              </div>
              <h2 className="text-xl font-bold text-fintech-text">Our Story</h2>
            </div>
            <p className="text-fintech-textMuted leading-relaxed mb-4">
              Founded by a team of procurement veterans, Kenluk Procurement was born from the need for
              faster, more secure international vendor management. We understand the challenges businesses
              face when operating globally, and we're here to solve them with technology.
            </p>
            <p className="text-fintech-textMuted leading-relaxed text-sm">
              What started as a solution for Nigerian enterprises has grown into a platform serving
              businesses across Africa and beyond.
            </p>
          </div>
        </div>

        {/* Timeline */}
        <div className="mb-16">
          <div className="text-center mb-10 public-readable-panel">
            <h2 className="text-2xl font-bold text-fintech-text mb-3">Our Journey</h2>
            <p className="text-fintech-textMuted">Key milestones in our growth story.</p>
          </div>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 sm:left-1/2 top-0 bottom-0 w-px bg-fintech-border -translate-x-1/2 sm:block" />
            <div className="flex flex-col gap-8">
              {milestones.map((milestone, i) => (
                <div key={i} className={`flex items-start gap-4 sm:gap-0 relative ${i % 2 === 0 ? 'sm:flex-row' : 'sm:flex-row-reverse'}`}>
                  <div className={`flex-1 ${i % 2 === 0 ? 'sm:pr-10 sm:text-right' : 'sm:pl-10'} pl-10 sm:pl-0`}>
                    <div className={`card p-5 inline-block w-full sm:max-w-xs ${i % 2 === 0 ? 'sm:ml-auto' : ''}`}>
                      <span className="text-xs font-bold text-fintech-secondary uppercase tracking-wider">{milestone.year}</span>
                      <p className="text-sm text-fintech-text mt-1 leading-relaxed">{milestone.event}</p>
                    </div>
                  </div>
                  {/* Center dot */}
                  <div className="absolute left-4 sm:left-1/2 transform -translate-x-1/2 w-4 h-4 rounded-full bg-fintech-primary border-2 border-white shadow-sm flex-shrink-0 mt-4 z-10" />
                  <div className="flex-1 hidden sm:block" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="mb-16">
          <div className="text-center mb-10 public-readable-panel">
            <h2 className="text-2xl font-bold text-fintech-text mb-3">Our Core Values</h2>
            <p className="text-fintech-textMuted">The principles that guide everything we build and every decision we make.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((value, i) => (
              <div key={i} className="card p-6 text-center hover:shadow-md transition-shadow group">
                <div className={`w-12 h-12 rounded-xl ${value.bg} border ${value.border} ${value.color} flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  {value.icon}
                </div>
                <h3 className="font-semibold text-fintech-text mb-2">{value.title}</h3>
                <p className="text-sm text-fintech-textMuted leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Team section */}
        <div className="card p-8 text-center bg-gradient-to-br from-white/92 to-blue-50/70">
          <div className="w-12 h-12 rounded-xl bg-white border border-fintech-border text-fintech-textMuted flex items-center justify-center mx-auto mb-4 shadow-sm">
            <Users size={22} />
          </div>
          <h2 className="text-xl font-bold text-fintech-text mb-3">Built by Experts</h2>
          <p className="text-fintech-textMuted max-w-xl mx-auto leading-relaxed mb-6">
            Our team combines deep expertise in fintech, procurement, and enterprise software to deliver
            a platform that truly understands the needs of modern businesses.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            {['Fintech Veterans', 'Procurement Experts', 'Enterprise Engineers', 'Compliance Specialists'].map((role) => (
              <span key={role} className="px-3 py-1.5 rounded-full bg-white border border-fintech-border text-xs font-medium text-fintech-textMuted">
                {role}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
